/**
 * Disclosure Progress Calculation
 *
 * Calculates completion status for all disclosure categories.
 * Used by the Disclosure Hub to show overall progress.
 *
 * Document sources are unified:
 * - `uploaded_documents` (Profile tab): AI extraction pipeline
 * - `disclosure_documents` (legacy): Direct disclosure uploads
 * Both sources count toward disclosure compliance.
 */

import { supabase } from '@/lib/supabase/client';

/**
 * Maps uploaded_documents.document_type → disclosure category
 * This allows documents uploaded in the Profile tab to count toward Disclosure compliance
 */
const DOC_TYPE_TO_DISCLOSURE_CATEGORY: Record<string, string> = {
  // Income documents
  'pay_stub': 'pay_stubs',
  // Bank documents
  'bank_statement': 'bank_accounts',
  // Investment documents
  'investment_statement': 'investments',
  'retirement_statement': 'retirement',
  // Tax documents
  'tax_return': 'tax_returns',
  // Debt documents
  'mortgage_statement': 'mortgages',
  'credit_card_statement': 'credit_cards',
  // Property documents
  'property_deed': 'real_estate',
  'vehicle_title': 'vehicles',
  'appraisal': 'real_estate',
  // Loan documents
  'loan_document': 'promissory_notes',
  // ─── New mappings for FL 12.285 parity (#1256) ─────────────
  'w2': 'w2_1099_k1',
  '1099': 'w2_1099_k1',
  'k1': 'w2_1099_k1',
  'business_tax_return': 'business_tax_returns',
  'loan_application': 'loan_applications',
  'financial_statement': 'loan_applications',
  'income_statement': 'income_statement',
  'premarital_agreement': 'premarital_agreements',
  'court_order': 'court_support_orders',
  'financial_affidavit': 'financial_affidavit',
};

/**
 * Maps asset_type values from the assets table to disclosure categories
 * These must match the values defined in getAssetTypes() in intake/page.tsx
 */
const ASSET_TYPE_TO_CATEGORY: Record<string, string> = {
  // Real Estate
  'primary_residence': 'real_estate',
  'rental_property': 'real_estate',
  'vacation_property': 'real_estate',
  'land': 'real_estate',
  'other_real_estate': 'real_estate',
  // Vehicles
  'vehicle': 'vehicles',
  'boat': 'vehicles',
  'rv': 'vehicles',
  'motorcycle': 'vehicles',
  'other_vehicle': 'vehicles',
  // Bank Accounts
  'checking': 'bank_accounts',
  'savings': 'bank_accounts',
  'money_market': 'bank_accounts',
  'cd': 'bank_accounts',
  // Investments (includes equity compensation)
  'brokerage': 'investments',
  'stocks': 'investments',
  'bonds': 'investments',
  'mutual_funds': 'investments',
  'etf': 'investments',
  'other_investment': 'investments',
  'stock_options': 'investments',
  'rsu': 'investments',
  'equity_compensation': 'investments',
  // Retirement
  '401k': 'retirement',
  '403b': 'retirement',
  'ira': 'retirement',
  'roth_ira': 'retirement',
  'pension': 'retirement',
  'other_retirement': 'retirement',
  // Business interests
  'llc': 'business',
  'corporation': 'business',
  's_corp': 'business',
  'partnership': 'business',
  'sole_prop': 'business',
  'professional_practice': 'business',
  // Crypto
  'crypto': 'crypto',
  // Insurance with cash value
  'life_insurance_cash_value': 'life_insurance',
  'annuity': 'life_insurance',
  // Note: Personal property (jewelry, art, collectibles, furniture, electronics)
  // and other assets (intellectual_property, royalties, other) don't map to
  // specific disclosure categories - they're tracked in general assets
};

/**
 * Maps debt_type values from the debts table to disclosure categories
 * These must match the values defined in getDebtTypes() in intake/page.tsx
 */
const DEBT_TYPE_TO_CATEGORY: Record<string, string> = {
  // Mortgages
  'mortgage': 'mortgages',
  'heloc': 'mortgages',
  'home_equity_loan': 'mortgages',
  // Credit cards
  'credit_card': 'credit_cards',
  'business_credit_card': 'credit_cards',
  'line_of_credit': 'credit_cards',
  // Promissory notes / loans
  'personal_loan': 'promissory_notes',
  'auto_loan': 'promissory_notes',
  'boat_loan': 'promissory_notes',
  'rv_loan': 'promissory_notes',
  'student_loan': 'promissory_notes',
  'student_loan_federal': 'promissory_notes',
  'student_loan_private': 'promissory_notes',
  'medical_debt': 'promissory_notes',
  'dental_debt': 'promissory_notes',
  'business_loan': 'promissory_notes',
  'judgment': 'promissory_notes',
  'legal_fees': 'promissory_notes',
  'family_loan': 'promissory_notes',
  'other': 'promissory_notes',
  // Tax debts map to contingent liabilities
  'tax_debt_federal': 'contingent_liabilities',
  'tax_debt_state': 'contingent_liabilities',
};

/** Shape of one DISCLOSURE_CATEGORIES entry (keys stay inferred via `satisfies`, so DisclosureCategory is unchanged). */
export interface DisclosureCategoryMeta {
  name: string;
  description: string;
  icon: string;
  requiredMonths?: number;
  requiredYears?: number;
  plaidEnabled: boolean;
  manualFormKey: string | null;
  importEnabled: boolean;
  payrollEnabled?: boolean;
}

/**
 * Disclosure categories matching FL-142 requirements
 */
/** The disclosure category keys, in FL-142 order. Kept explicit so the map below is fully typed. */
export type DisclosureCategory =
  'bank_accounts' |
  'credit_cards' |
  'investments' |
  'retirement' |
  'third_party_payments' |
  'crypto' |
  'pay_stubs' |
  'tax_returns' |
  'ssa_statement' |
  'real_estate' |
  'vehicles' |
  'life_insurance' |
  'health_insurance' |
  'pc_insurance' |
  'mortgages' |
  'promissory_notes' |
  'leases' |
  'credit_report' |
  'business' |
  'contingent_liabilities' |
  'financial_affidavit' |
  'income_statement' |
  'loan_applications' |
  'premarital_agreements' |
  'modification_agreements' |
  'unequal_distribution_claims' |
  'court_support_orders' |
  'business_tax_returns' |
  'w2_1099_k1';

export const DISCLOSURE_CATEGORIES: Record<DisclosureCategory, DisclosureCategoryMeta> = {
  bank_accounts: {
    name: 'Bank Accounts',
    description: 'Checking, savings, and money market accounts',
    icon: 'Building2',
    requiredMonths: 12,
    plaidEnabled: true,
    manualFormKey: 'bank',
    importEnabled: false,
  },
  credit_cards: {
    name: 'Credit Cards',
    description: 'All credit card accounts and statements',
    icon: 'CreditCard',
    requiredMonths: 24,
    plaidEnabled: true,
    manualFormKey: 'debt',
    importEnabled: false,
  },
  investments: {
    name: 'Investment Accounts',
    description: 'Brokerage, stocks, bonds, mutual funds',
    icon: 'TrendingUp',
    requiredMonths: 12,
    plaidEnabled: true,
    manualFormKey: 'asset',
    importEnabled: false,
  },
  retirement: {
    name: 'Retirement Accounts',
    description: '401(k), IRA, pension, and other retirement accounts',
    icon: 'PiggyBank',
    requiredMonths: 12,
    plaidEnabled: true,
    manualFormKey: 'pension',
    importEnabled: false,
  },
  third_party_payments: {
    name: 'Third-Party Payments',
    description: 'Venmo, PayPal, CashApp, Zelle transactions',
    icon: 'Smartphone',
    plaidEnabled: false,
    manualFormKey: 'thirdParty',
    importEnabled: true,
  },
  crypto: {
    name: 'Cryptocurrency',
    description: 'Virtual currency statements (12 months) plus a listing of all current holdings',
    icon: 'Bitcoin',
    // #1510: FL 12.285 requires 12 months of virtual-currency statements —
    // the certificate certifies it, so coverage must actually check it.
    // (third_party_payments stays point-in-time: not an FL category.)
    requiredMonths: 12,
    plaidEnabled: false,
    manualFormKey: 'crypto',
    importEnabled: true,
  },
  pay_stubs: {
    name: 'Pay Stubs',
    description: 'Recent pay stubs (typically last 6 months)',
    icon: 'FileText',
    requiredMonths: 6,
    plaidEnabled: false,
    payrollEnabled: true,
    manualFormKey: 'income',
    importEnabled: false,
  },
  tax_returns: {
    name: 'Tax Returns',
    description: 'Federal and state tax returns (last 3 years)',
    icon: 'FileSpreadsheet',
    requiredYears: 3,
    plaidEnabled: false,
    manualFormKey: 'tax',
    importEnabled: false,
  },
  ssa_statement: {
    name: 'Social Security Statement',
    description: 'Latest Social Security statement',
    icon: 'FileCheck',
    plaidEnabled: false,
    manualFormKey: 'ssa',
    importEnabled: false,
  },
  real_estate: {
    name: 'Real Estate',
    description: 'Houses, land, and rental properties',
    icon: 'Home',
    plaidEnabled: false,
    manualFormKey: 'asset',
    importEnabled: false,
  },
  vehicles: {
    name: 'Vehicles',
    description: 'Cars, boats, motorcycles, RVs',
    icon: 'Car',
    plaidEnabled: false,
    manualFormKey: 'asset',
    importEnabled: false,
  },
  life_insurance: {
    name: 'Life Insurance',
    description: 'Term and whole life insurance policies',
    icon: 'Shield',
    plaidEnabled: false,
    manualFormKey: 'lifeInsurance',
    importEnabled: false,
  },
  health_insurance: {
    name: 'Health Insurance',
    description: 'Medical, dental, and vision coverage',
    icon: 'Heart',
    plaidEnabled: false,
    manualFormKey: 'healthInsurance',
    importEnabled: false,
  },
  pc_insurance: {
    name: 'Property/Casualty Insurance',
    description: 'Homeowners, auto, umbrella policies',
    icon: 'ShieldCheck',
    plaidEnabled: false,
    manualFormKey: 'pcInsurance',
    importEnabled: false,
  },
  mortgages: {
    name: 'Mortgages',
    description: 'Home loans and HELOCs',
    icon: 'Building',
    plaidEnabled: true,
    manualFormKey: 'debt',
    importEnabled: false,
  },
  promissory_notes: {
    name: 'Promissory Notes',
    description: 'Loans owed to or by you',
    icon: 'FileSignature',
    plaidEnabled: false,
    manualFormKey: 'promissoryNote',
    importEnabled: false,
  },
  leases: {
    name: 'Lease Agreements',
    description: 'Rental or equipment leases',
    icon: 'FileKey',
    plaidEnabled: false,
    manualFormKey: 'lease',
    importEnabled: false,
  },
  credit_report: {
    name: 'Credit Report',
    description: 'Current credit report from major bureaus',
    icon: 'FileBarChart',
    plaidEnabled: false,
    manualFormKey: null,
    importEnabled: false,
  },
  business: {
    name: 'Business Interests',
    description: 'Business ownership, K-1s, and returns',
    icon: 'Briefcase',
    plaidEnabled: false,
    manualFormKey: 'business',
    importEnabled: false,
  },
  contingent_liabilities: {
    name: 'Contingent Liabilities',
    description: 'Pending lawsuits, tax audits, disputed debts',
    icon: 'AlertTriangle',
    plaidEnabled: false,
    manualFormKey: 'contingentLiability',
    importEnabled: false,
  },
  // ─── FL 12.285 categories added for parity (#1256) ─────────────
  financial_affidavit: {
    name: 'Financial Affidavit',
    description: 'Financial Affidavit produced under separate cover',
    icon: 'FileText',
    plaidEnabled: false,
    manualFormKey: null,
    importEnabled: false,
  },
  income_statement: {
    name: 'Income Statement',
    description: 'Income not reflected on pay stubs (bonuses, commissions, tips, etc.)',
    icon: 'FileText',
    requiredMonths: 6,
    plaidEnabled: false,
    manualFormKey: null,
    importEnabled: false,
  },
  w2_1099_k1: {
    name: 'W-2, 1099, K-1 Forms',
    description: 'IRS forms W-2, 1099, K-1 for the past year',
    icon: 'FileSpreadsheet',
    requiredYears: 1,
    plaidEnabled: false,
    manualFormKey: null,
    importEnabled: false,
  },
  loan_applications: {
    name: 'Loan Applications / Financial Statements',
    description: 'Loan applications, financial statements, credit reports',
    icon: 'FileSignature',
    requiredMonths: 24,
    plaidEnabled: false,
    manualFormKey: null,
    importEnabled: false,
  },
  premarital_agreements: {
    name: 'Premarital / Marital Agreements',
    description: 'Premarital and marital agreements, non-paternity affidavits',
    icon: 'FileKey',
    plaidEnabled: false,
    manualFormKey: null,
    importEnabled: false,
  },
  // #1216: final 2 FL 12.285 categories from the HHD filed template.
  // Both single-doc coverage (no periods); the "if a modification proceeding"
  // condition lives in the statutory narrative, exactly as the filed form
  // words it.
  modification_agreements: {
    name: 'Modification Proceeding Agreements',
    description: 'Written agreements between the parties since the order being modified (modification proceedings only)',
    icon: 'FileClock',
    plaidEnabled: false,
    manualFormKey: null,
    importEnabled: false,
  },
  unequal_distribution_claims: {
    name: 'Unequal Distribution / Nonmarital Claims',
    description: 'Documents supporting unequal-distribution, enhancement, or nonmarital-status claims',
    icon: 'Scale',
    plaidEnabled: false,
    manualFormKey: null,
    importEnabled: false,
  },
  court_support_orders: {
    name: 'Court Orders for Support',
    description: 'Court orders directing spousal support or child support',
    icon: 'Scale',
    plaidEnabled: false,
    manualFormKey: null,
    importEnabled: false,
  },
  business_tax_returns: {
    name: 'Business Tax Returns',
    description: 'Corporate, partnership, and trust tax returns',
    icon: 'Briefcase',
    requiredYears: 3,
    plaidEnabled: false,
    manualFormKey: null,
    importEnabled: false,
  },
};


export interface CategoryStatus {
  status: 'not_started' | 'partial' | 'complete' | 'confirmed_none';
  itemCount: number;
  requiredCount?: number;
  documentsCount: number;
  requiredDocuments?: number;
  periodsUploaded?: number;
  periodsRequired?: number;
  lastUpdated?: string;
  /** True if user has confirmed they have nothing to disclose for this category */
  confirmedNone?: boolean;
}

export interface DisclosureProgress {
  userId: string;
  categories: Record<DisclosureCategory, CategoryStatus>;
  totalItems: number;
  completedItems: number;
  completionPercentage: number;
  filingDate?: string;
  disclosureDeadline?: string;
  daysUntilDeadline?: number;
  attestationSigned: boolean;
  lastUpdated: string;
  /** Categories where user has confirmed they have nothing to disclose */
  confirmedNoneCategories: DisclosureCategory[];
}

/**
 * Calculate disclosure progress for a user
 */
export async function calculateDisclosureProgress(userId: string): Promise<DisclosureProgress> {
  const categories: Record<string, CategoryStatus> = {};

  // Initialize all categories
  for (const key of Object.keys(DISCLOSURE_CATEGORIES)) {
    categories[key] = {
      status: 'not_started',
      itemCount: 0,
      documentsCount: 0,
    };
  }

  // Get disclosure progress record
  const { data: progressRecord } = await supabase
    .from('disclosure_progress')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  // Get counts from various tables
  // Note: We use comprehensive mapping for assets/debts, plus specific table counts for
  // dedicated tables (life_insurance_policies, crypto_holdings, businesses, etc.)
  const [
    { count: plaidBankCount },
    { count: plaidInvestmentCount },
    { count: plaidCreditCount },
    { count: lifeInsuranceCount },
    { count: thirdPartyCount },
    { count: cryptoCount },
    { count: incomeSourceCount },
    { count: businessCount },
    { count: contingentItemsCount },
    { data: documents },
    { count: taxDocumentsCount },
    { data: uploadedDocuments },
  ] = await Promise.all([
    // Plaid accounts by type - depository (checking/savings)
    supabase.from('plaid_accounts').select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .in('type', ['depository']),
    // Plaid accounts by type - investments
    supabase.from('plaid_accounts').select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .in('type', ['investment', 'brokerage']),
    // Plaid credit accounts
    supabase.from('plaid_accounts').select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .in('type', ['credit']),
    // Dedicated tables for specific categories
    supabase.from('life_insurance_policies').select('*', { count: 'exact', head: true })
      .eq('user_id', userId),
    supabase.from('third_party_payment_accounts').select('*', { count: 'exact', head: true })
      .eq('user_id', userId),
    supabase.from('crypto_holdings').select('*', { count: 'exact', head: true })
      .eq('user_id', userId),
    supabase.from('income_sources').select('*', { count: 'exact', head: true })
      .eq('user_id', userId),
    supabase.from('businesses').select('*', { count: 'exact', head: true })
      .eq('user_id', userId),
    supabase.from('contingent_items').select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('item_type', 'liability'),
    // Documents from legacy disclosure_documents table
    supabase.from('disclosure_documents').select('category, subcategory')
      .eq('user_id', userId),
    // Tax documents from dedicated tax_documents table
    supabase.from('tax_documents').select('*', { count: 'exact', head: true })
      .eq('user_id', userId),
    // Uploaded documents from Profile tab (unified document upload)
    supabase.from('uploaded_documents').select('document_type')
      .eq('user_id', userId)
      .eq('processing_status', 'complete'),
  ]);

  // Count ALL assets by their disclosure category using the mapping
  // This properly maps asset_types like 'primary_residence' → 'real_estate', 'crypto' → 'crypto', etc.
  const { data: assetsByType } = await supabase
    .from('assets')
    .select('asset_type')
    .eq('user_id', userId);

  const assetCategoryCounts: Record<string, number> = {};
  if (assetsByType) {
    for (const asset of assetsByType) {
      const disclosureCategory = ASSET_TYPE_TO_CATEGORY[asset.asset_type];
      if (disclosureCategory) {
        assetCategoryCounts[disclosureCategory] = (assetCategoryCounts[disclosureCategory] || 0) + 1;
      }
    }
  }

  // Count ALL debts by their disclosure category using the mapping
  const { data: debtsByType } = await supabase
    .from('debts')
    .select('debt_type')
    .eq('user_id', userId);

  const debtCategoryCounts: Record<string, number> = {};
  if (debtsByType) {
    for (const debt of debtsByType) {
      const disclosureCategory = DEBT_TYPE_TO_CATEGORY[debt.debt_type];
      if (disclosureCategory) {
        debtCategoryCounts[disclosureCategory] = (debtCategoryCounts[disclosureCategory] || 0) + 1;
      }
    }
  }

  // Map counts to categories - combining Plaid accounts + manual entries from assets/debts tables
  // Bank accounts: Plaid depository + assets (checking, savings, money_market, cd)
  categories.bank_accounts.itemCount = (plaidBankCount || 0) + (assetCategoryCounts['bank_accounts'] || 0);

  // Investments: Plaid investment/brokerage + assets (brokerage, stocks, bonds, ETFs, equity compensation)
  categories.investments.itemCount = (plaidInvestmentCount || 0) + (assetCategoryCounts['investments'] || 0);

  // Retirement: assets (401k, 403b, IRA, pension, etc.)
  categories.retirement.itemCount = assetCategoryCounts['retirement'] || 0;

  // Credit cards: Plaid credit accounts + debts (credit_card, business_credit_card, line_of_credit)
  categories.credit_cards.itemCount = (plaidCreditCount || 0) + (debtCategoryCounts['credit_cards'] || 0);

  // Mortgages: debts (mortgage, heloc, home_equity_loan)
  categories.mortgages.itemCount = debtCategoryCounts['mortgages'] || 0;

  // Promissory notes: debts (personal_loan, auto_loan, student_loan, etc.)
  // Note: Documents added later in the code
  categories.promissory_notes.itemCount = debtCategoryCounts['promissory_notes'] || 0;

  // Third-party payments: third_party_payment_accounts table
  categories.third_party_payments.itemCount = thirdPartyCount || 0;

  // Crypto: crypto_holdings table + assets with type 'crypto'
  categories.crypto.itemCount = (cryptoCount || 0) + (assetCategoryCounts['crypto'] || 0);

  // Life insurance: life_insurance_policies table + assets (life_insurance_cash_value, annuity)
  categories.life_insurance.itemCount = (lifeInsuranceCount || 0) + (assetCategoryCounts['life_insurance'] || 0);

  // Business: businesses table + assets (llc, corporation, s_corp, partnership, etc.)
  categories.business.itemCount = (businessCount || 0) + (assetCategoryCounts['business'] || 0);

  // Contingent liabilities: contingent_items table + tax debts
  categories.contingent_liabilities.itemCount = (contingentItemsCount || 0) + (debtCategoryCounts['contingent_liabilities'] || 0);

  // Real estate: assets (primary_residence, rental_property, vacation_property, land, etc.)
  categories.real_estate.itemCount = assetCategoryCounts['real_estate'] || 0;

  // Vehicles: assets (vehicle, boat, rv, motorcycle, etc.)
  categories.vehicles.itemCount = assetCategoryCounts['vehicles'] || 0;

  // Count documents from uploaded_documents (unified document source from Profile tab)
  // This allows documents uploaded in one place to count toward disclosure compliance
  const uploadedDocCounts: Record<string, number> = {};
  if (uploadedDocuments) {
    for (const doc of uploadedDocuments) {
      if (doc.document_type) {
        // Map document_type to disclosure category
        const disclosureCategory = DOC_TYPE_TO_DISCLOSURE_CATEGORY[doc.document_type];
        if (disclosureCategory) {
          uploadedDocCounts[disclosureCategory] = (uploadedDocCounts[disclosureCategory] || 0) + 1;
        }
      }
    }
  }

  // Count documents by category from disclosure_documents (legacy system)
  const docCounts: Record<string, number> = {};
  if (documents) {
    for (const doc of documents) {
      const cat = doc.category;
      docCounts[cat] = (docCounts[cat] || 0) + 1;
    }
  }

  // Helper to get combined document count from both sources
  const getCombinedDocCount = (legacyCategory: string, disclosureCategory: string): number => {
    return (docCounts[legacyCategory] || 0) + (uploadedDocCounts[disclosureCategory] || 0);
  };

  // Map document categories to disclosure categories (combining both sources)
  categories.bank_accounts.documentsCount = getCombinedDocCount('bank_statement', 'bank_accounts');
  categories.credit_cards.documentsCount = getCombinedDocCount('credit_card_statement', 'credit_cards');
  categories.investments.documentsCount = getCombinedDocCount('investment_statement', 'investments');
  categories.retirement.documentsCount = getCombinedDocCount('retirement_statement', 'retirement');

  // Combine disclosure_documents tax_return category with dedicated tax_documents table and uploaded documents
  const disclosureTaxDocs = (docCounts['tax_return'] || 0);
  categories.tax_returns.documentsCount = disclosureTaxDocs + (taxDocumentsCount || 0) + (uploadedDocCounts['tax_returns'] || 0);
  categories.tax_returns.itemCount = disclosureTaxDocs + (taxDocumentsCount || 0) + (uploadedDocCounts['tax_returns'] || 0);

  // Pay stubs - combine both document sources plus income sources
  const payStubDocs = getCombinedDocCount('pay_stub', 'pay_stubs');
  categories.pay_stubs.documentsCount = payStubDocs;
  // Pay stubs item count = income sources + all pay stub documents
  categories.pay_stubs.itemCount = (incomeSourceCount || 0) + payStubDocs;

  categories.ssa_statement.documentsCount = docCounts['ssa_statement'] || 0;
  categories.ssa_statement.itemCount = docCounts['ssa_statement'] || 0;

  categories.real_estate.documentsCount = getCombinedDocCount('deed', 'real_estate');
  categories.vehicles.documentsCount = getCombinedDocCount('vehicle_title', 'vehicles');

  categories.credit_report.documentsCount = docCounts['credit_report'] || 0;
  categories.credit_report.itemCount = docCounts['credit_report'] || 0;

  categories.life_insurance.documentsCount = docCounts['life_insurance'] || 0;
  categories.health_insurance.documentsCount = docCounts['health_insurance'] || 0;
  categories.pc_insurance.documentsCount = docCounts['insurance_declaration'] || 0;

  categories.leases.documentsCount = docCounts['lease_agreement'] || 0;
  categories.leases.itemCount = docCounts['lease_agreement'] || 0;

  categories.promissory_notes.documentsCount = getCombinedDocCount('promissory_note', 'promissory_notes');
  // Promissory notes: debt items already counted above, add any uploaded documents to itemCount
  categories.promissory_notes.itemCount += getCombinedDocCount('promissory_note', 'promissory_notes');

  categories.mortgages.documentsCount = uploadedDocCounts['mortgages'] || 0;

  // Get confirmed_none categories from progress record
  const confirmedNoneCategories: DisclosureCategory[] =
    (progressRecord?.confirmed_none_categories as string[] || []) as DisclosureCategory[];

  // Determine status for each category
  let completedCategories = 0;
  const totalCategories = Object.keys(DISCLOSURE_CATEGORIES).length;

  for (const [key, cat] of Object.entries(categories)) {
    const categoryKey = key as DisclosureCategory;
    const categoryDef = DISCLOSURE_CATEGORIES[categoryKey];
    const hasItems = cat.itemCount > 0 || cat.documentsCount > 0;
    const isConfirmedNone = confirmedNoneCategories.includes(categoryKey);

    // If user confirmed they have nothing, mark as confirmed_none (counts as complete)
    if (isConfirmedNone && !hasItems) {
      cat.status = 'confirmed_none';
      cat.confirmedNone = true;
    } else if (!hasItems) {
      cat.status = 'not_started';
    } else {
      // Check if requirements are met
      const requiredMonths = 'requiredMonths' in categoryDef ? categoryDef.requiredMonths : undefined;
      const requiredYears = 'requiredYears' in categoryDef ? categoryDef.requiredYears : undefined;
      const requiresDocuments = requiredMonths || requiredYears;

      if (requiresDocuments) {
        // For time-series categories, check document coverage
        // Note: requiredYears means yearly documents (like tax returns) = number of documents needed
        // requiredMonths means monthly documents (like bank statements) = number of documents needed
        const requiredDocuments = requiredMonths || requiredYears || 0;
        cat.periodsRequired = requiredDocuments;
        cat.periodsUploaded = cat.documentsCount;

        // Use the higher of itemCount or documentsCount to avoid double-counting
        // itemCount often includes documents already (e.g., pay_stubs = income_sources + docs)
        const effectiveCount = Math.max(cat.itemCount, cat.documentsCount);

        if (effectiveCount >= requiredDocuments) {
          cat.status = 'complete';
        } else if (effectiveCount > 0) {
          cat.status = 'partial';
        } else {
          cat.status = 'not_started';
        }
      } else {
        // For non-time-series, having items is complete
        cat.status = 'complete';
      }
    }

    // Both 'complete' and 'confirmed_none' count as completed for progress
    if (cat.status === 'complete' || cat.status === 'confirmed_none') {
      completedCategories++;
    }
  }

  // Calculate overall progress
  const completionPercentage = Math.round((completedCategories / totalCategories) * 100);

  // Calculate deadline
  let daysUntilDeadline: number | undefined;
  if (progressRecord?.disclosure_deadline) {
    const deadline = new Date(progressRecord.disclosure_deadline);
    const today = new Date();
    daysUntilDeadline = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }

  return {
    userId,
    categories: categories as Record<DisclosureCategory, CategoryStatus>,
    totalItems: Object.values(categories).reduce((sum, c) => sum + c.itemCount, 0),
    completedItems: completedCategories,
    completionPercentage,
    filingDate: progressRecord?.filing_date || undefined,
    disclosureDeadline: progressRecord?.disclosure_deadline || undefined,
    daysUntilDeadline,
    attestationSigned: progressRecord?.attestation_signed || false,
    confirmedNoneCategories,
    lastUpdated: new Date().toISOString(),
  };
}

/**
 * Update the disclosure progress record in the database
 */
export async function updateDisclosureProgress(userId: string, progress: DisclosureProgress): Promise<void> {
  const categoryStatus: Record<string, unknown> = {};
  for (const [key, status] of Object.entries(progress.categories)) {
    categoryStatus[key] = {
      status: status.status,
      items: status.itemCount,
      documents: status.documentsCount,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await (supabase.from('disclosure_progress') as any)
    .upsert({
      user_id: userId,
      category_status: categoryStatus,
      items_complete: progress.completedItems,
      completion_percentage: progress.completionPercentage,
      updated_at: new Date().toISOString(),
    }, {
      onConflict: 'user_id',
    });
}
